import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { IDataSourceItem, IPlaceholders, isJsonArray, Json, JsonObject, JsonPrimitive } from '@se/cp.common';
import {
    CheckedDataService,
    ConstraintTypeEnum,
    DataBinding,
    DataBindingPart,
    Guid,
    IConstraint,
    Logger,
    SeConstraintConfiguratorService,
    SeConstraintNotifierService,
    SetValueOptions,
} from '@se/se.platform.base';

import { cloneDeep, differenceWith, find, isEmpty, isEqual, merge } from 'lodash-es';
import moment, { isMoment, Moment } from 'moment';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';

import {
    CpEntity,
    CpEntityList,
    CpUndoEntity,
    ICpEntity,
    ICpEntityFactory,
    isArrayOfEntities,
    isEntity,
    isPropertyLeaf,
    isPropertyPrimitive,
    PropertyType,
} from '../cp-configuration/view-model/cp-entity';
import { CpDatasetService } from '../cp-dataset-service';
import { CpDatasourceService } from '../cp-datasource.service';

const defaultSetValueOptions: SetValueOptions = {
    emitEvent: true,
    autoUpdated: false,
    force: false,
};

@Injectable({
    providedIn: 'root',
})
export class CpModelService implements CheckedDataService, ICpEntityFactory {
    private rootForm = new BehaviorSubject<FormGroup | null>(null);

    private model: ICpEntity | undefined = undefined;
    private modelListeners: Map<string, BehaviorSubject<PropertyType | undefined>> = new Map();

    private placeholders!: IPlaceholders;

    private bindingsUnderUpdate = new Map<string, boolean>();

    constructor(
        private constraintNotifier: SeConstraintNotifierService,
        private constraintConfiguratorService: SeConstraintConfiguratorService,
        private datasetService: CpDatasetService,
        private dataSourceService: CpDatasourceService,
        private logger: Logger,
    ) {}

    safeExit() {
        const oldRoot = this.model;
        for (const listener of this.modelListeners.values()) {
            listener.complete();
        }
        this.modelListeners = new Map();
        this.model = undefined;
        oldRoot?.destroy();
        this.rootForm.next(null);
        this.constraintConfiguratorService.initializeConstraints([], of(false));
    }

    getAsJson(): any {
        return this.model?.toJSON() ?? null;
    }

    getEntity(binding: DataBinding, createMissingEntities = false): ICpEntity | undefined {
        return this.model?.getEntity(binding, createMissingEntities);
    }

    getValue(binding: DataBinding): PropertyType | undefined {
        return this.model?.getValue(binding);
    }

    getRootForm() {
        return this.rootForm.asObservable();
    }

    initialize(model: JsonObject, constraints: IConstraint[], placeholders: IPlaceholders) {
        if (!this.rootForm.value) this.rootForm.next(new FormGroup({}));
        this.placeholders = placeholders;
        const dataRoot = this.makeEntity(model) as ICpEntity;
        if (this.model) {
            this.model.destroy();
        }
        this.model = dataRoot;
        const isApproved$ = this.register(new DataBinding('Report.ReferenceExamination.IsApproved')).pipe(
            map((value) => !!value),
        );

        /* this.constraintConfiguratorService.initializeConstraints(
            isApproved
                ? constraints.filter(
                      (c) =>
                          ![
                              ConstraintTypeEnum.Calculation,
                              ConstraintTypeEnum.CustomValue,
                              ConstraintTypeEnum.NormalValue,
                          ].includes(c.constraintType),
                  )
                : constraints,
        ); */
        this.constraintConfiguratorService.initializeConstraints(constraints, isApproved$);
        this.constraintConfiguratorService.initializeCheckedDataServices([this, this.dataSourceService]);
        setTimeout(() => this.notifyAllBindingsAndConstraints());
    }

    initializeConstraints(constraints: IConstraint[]) {
        const isApproved$ = this.register(new DataBinding('Report.ReferenceExamination.IsApproved')).pipe(
            map((value) => !!value),
        );
        this.constraintConfiguratorService.initializeConstraints(constraints, isApproved$);
        this.constraintConfiguratorService.initializeCheckedDataServices([this, this.dataSourceService]);
        this.constraintNotifier.notifyAllConstraint();
    }

    isModified() {
        return this.model?.isModified ?? false;
    }

    isValid() {
        return !!this.rootForm.value?.valid;
    }

    getModelAndValidity() {
        return combineLatest([
            // Qua recuperiamo le modifiche al modello ed alla sua validità...
            this.registerRoot().pipe(
                switchMap((root) => {
                    if (!root) {
                        return of([undefined, false] as const);
                    }
                    return root.isValid$.pipe(map((isValid) => [root, isValid] as const));
                }),
            ),
            // ...mentre qua recuperiamo i cambiamenti alla validità del form.
            this.getRootForm().pipe(
                switchMap((form) => form?.statusChanges ?? of('INVALID')),
                startWith('INVALID'),
            ),
        ]).pipe(
            map(([[root, isModelValid], formStatus]) => {
                return [root, isModelValid && formStatus !== 'INVALID'] as const;
            }),
        );
    }

    getModelAndValiditySync() {
        return [this.model, (this.model?.isValid ?? false) && this.rootForm.value?.valid] as const;
    }

    register(binding: DataBinding): Observable<PropertyType | undefined> {
        const key = binding.toString();
        let subject = this.modelListeners.get(key);
        if (!subject) {
            subject = new BehaviorSubject<PropertyType | undefined>(this.model?.getValue(binding));
            this.modelListeners.set(key, subject);
        }
        return subject.asObservable();
    }

    registerEntity(binding: DataBinding, createMissingEntities = true): Observable<ICpEntity | undefined> {
        let entity = this.model?.getValue(binding);

        if (createMissingEntities && !entity) {
            entity = this.model?.getEntity(binding, true);
            if (entity) {
                this.notifyBindingsAndConstraints(binding, entity);
            }
        }

        const key = binding.toString();
        let subject = this.modelListeners.get(key);
        if (!subject) {
            subject = new BehaviorSubject<PropertyType | undefined>(entity);
            this.modelListeners.set(key, subject);
        }

        return subject.asObservable().pipe(
            map((e) => {
                if (!isEntity(e)) {
                    return undefined;
                }
                return e;
            }),
        );
    }

    registerList(
        binding: DataBinding,
        deepComparison = false,
        createMissingEntities = true,
    ): Observable<CpEntityList | undefined> {
        return this.registerEntity(binding, createMissingEntities).pipe(
            map((entity) => {
                if (!entity) {
                    return undefined;
                }
                if (!(entity instanceof CpEntityList)) {
                    return this.makeEntity<CpEntityList>([entity], binding, entity.parent);
                }
                return entity;
            }),
            distinctUntilChanged((x, y) => {
                if (deepComparison) {
                    return false;
                }

                return x?.activeEntities === y?.activeEntities;
            }),
        );
    }

    registerRoot(): Observable<ICpEntity | undefined> {
        return this.registerEntity(new DataBinding(), false);
    }

    notifyRootChange(root: JsonObject) {
        const oldModel = this.model;
        const newModel = this.makeEntity(root);

        this.model = newModel;
        this.notifyAllBindingsAndConstraints();

        oldModel?.destroy();

        const isApproved = this.model?.getValue(new DataBinding('Report.ReferenceExamination.IsApproved'));
        if (isApproved) {
            this.model.flushAutoUpdatedRecursive();
        }
    }

    /**
     * TabSelector
     *  - JSonData.Fetal.Fetus
     *      fetal { childrenObject: fetus { childrenArray {...} } }
     * Controllo normale
     *  - JSonData.Vascular
     *
     */
    createModelFromPlaceholder(
        targetPath: DataBinding,
        placeholderPath: string,
        ...initialValues: { name: string; value: JsonPrimitive | Moment }[]
    ): ICpEntity {
        const parentNode = this.datasetService.getNode(targetPath.entity);
        const isChildrenArray = !!parentNode?.childrenArray.find((n) => n.name === targetPath.property.toString());

        let target: ICpEntity | undefined;
        if (isChildrenArray) {
            target = this.getEntity(targetPath, true);
        } else {
            target = this.getEntity(targetPath.entity, true);
        }

        /*77954 forzare l'id nel caso di dynamicdatasource per evitare che venga rimosso nel toJSON*/
        const datasetNode = this.datasetService.getNode(targetPath);
        /** bug 124459 */
        //const placeholders = this.placeholders[placeholderPath];
        const datasetPlaceholders = this.datasetService.getPlaceholder(targetPath) ?? {};
        if (datasetNode?.isDynamicDataSource) {
            datasetPlaceholders.Id = new Guid().toString();
        }
        const newEntity = this.makeEntity(datasetPlaceholders, targetPath, target);
        for (const { name, value } of initialValues) {
            newEntity.setPropertyValue(
                name,
                this.makeEntity(value, new DataBinding(placeholderPath).addSimple(name), newEntity),
                false,
            );
        }

        if (target instanceof CpEntityList) {
            target.addEntityToList(new DataBinding(), newEntity);
            this.notifyBindingsAndConstraints(targetPath, target);
        } else {
            this.model?.setValue(targetPath, newEntity as any);
            this.notifyBindingsAndConstraints(targetPath, newEntity);
        }

        /*TODO: (34.12): ricostruiiamo il path partendo dal model service per ovviare alla mancanza degli indici nei children array
         * che vengono insertiti correttamente nel model service ma non hanno il path completo di indici*/
        const e: ICpEntity = newEntity as ICpEntity;
        e.path = new DataBinding(newEntity?.updatePath());
        return e;
    }

    removeEntity(entity: ICpEntity) {
        const path = entity.path?.entity;

        if (!!path && this.model?.remove(entity)) {
            const binding = entity.path?.toString();
            if (!!binding) {
                const listener = this.modelListeners.get(binding);
                listener?.next(null);
            }
            this.notifyBindingsAndConstraints(path, entity.parent);
        }
    }

    //#region CheckedDataService

    getEntities(binding: DataBinding): ICpEntity[] | undefined {
        return this.model?.getAllEntities(binding) || undefined;
    }

    getSelectedEntity(binding: DataBinding, createMissingEntities?: boolean): ICpEntity | undefined {
        return this.model?.getSelectedEntity(binding, createMissingEntities);
    }

    setValue(path: DataBinding, value: PropertyType, partialOptions: Partial<SetValueOptions> = {}) {
        const options = merge({}, defaultSetValueOptions, partialOptions);
        const strPath = path.toString();

        const isBindingUnderUpdate = this.bindingsUnderUpdate.has(strPath);

        //controllo che quel binding non sia già sotto aggiornamento (utile per evitare ricorsività infinita per valori che modificano altri valori)
        if (isBindingUnderUpdate) {
            return false;
        }

        //inserisco il binding in input nell'elenco dei binding "sotto aggiornamento"
        this.bindingsUnderUpdate.set(strPath, true);

        try {
            const oldValue = this.model?.getValue(path);

            if (!options.force && this.isPropertyValueEqual(value, oldValue)) {
                return false;
            }

            let oldParent: ICpEntity | undefined = undefined;
            if (isEntity(oldValue)) {
                if (oldValue.parent) {
                    oldParent = oldValue.parent;
                }
            }
            if (!oldParent) {
                oldParent = this.createOrFindParent(path);
            }
            const valueProperty = this.makeEntity<PropertyType>(value, path, oldParent);

            if (path.property?.isIndex()) {
                const parsedValue = isEntity(value)
                    ? value.toJSON()
                    : value && typeof value === 'object'
                      ? JSON.stringify(value)
                      : value;
                const errorObj = {
                    description: 'Custom Page Warning 111769',
                    inputs: {
                        path: path.toString(),
                        value: parsedValue,
                        partialOptions,
                    },
                    trace: new Error().stack as string,
                };

                this.logger.error(errorObj, '11176900-0000-0000-0000-000000000000');

                //@ts-ignore
                if (window.showWarningAlertMessage) {
                    console.trace('Warning 111769', path, value);
                    alert(
                        `Custom Page Warning 111769. tentativo scrittura su indice. binding: ${path.toString()}. value:${parsedValue}`,
                    );
                }
            }

            this.model?.setValue(path, valueProperty, options.emitEvent, options.autoUpdated);

            this.notifyBindingsAndConstraints(path, valueProperty);
        } catch (err) {
            this.logger.error(err);
        } finally {
            //rimuovo il binding dall'elenco dei binding "sotto aggiornamento"
            this.bindingsUnderUpdate.delete(strPath);
        }

        return true;
    }

    setDatasourceValue(path: DataBinding, guid: string, idDataSource: string, emitEvent?: boolean) {
        if (!guid) {
            return this.setValue(path, null, { emitEvent });
        }

        const dataSource = this.dataSourceService.getById(idDataSource);
        if (!dataSource) {
            throw new Error(`Unknown dataSource: ${idDataSource}`);
        }
        const item = find(dataSource, (i) => i.Id === guid);
        if (!item) {
            throw new Error(`Unknown dataSource item: ${idDataSource} / ${guid}`);
        }

        // TODO: Any.
        return this.setValue(path, item as any, { emitEvent });
    }

    setPropertyValue(path: string, value: PropertyType, emitEvent?: boolean, autoUpdated?: boolean) {
        this.model?.setPropertyValue(path, value, emitEvent, autoUpdated);

        this.notifyBindingsAndConstraints(new DataBinding(path), value);
    }

    // #endregion

    //#region ICpEntityFactory

    getAdvancedBindingKeys(binding: DataBinding): string[] | undefined {
        return this.datasetService.getAdvancedBindingKeys(binding);
    }

    makeEntity<T extends PropertyType>(value: T, binding?: DataBinding, parent?: ICpEntity): T;
    makeEntity<_ extends CpEntity>(
        value: JsonObject | ICpEntity | IDataSourceItem | null,
        binding?: DataBinding,
        parent?: ICpEntity,
    ): CpEntity;
    makeEntity<_ extends CpEntityList>(
        value: JsonObject[] | ICpEntity[],
        binding?: DataBinding,
        parent?: ICpEntity,
    ): CpEntityList;
    makeEntity(
        value: Json | ICpEntity[] | ICpEntity | IDataSourceItem | Moment,
        binding = new DataBinding(),
        parent?: ICpEntity,
    ): PropertyType {
        if (isPropertyLeaf(value)) {
            return value;
        }
        if (isEntity(value)) {
            value.parent = value.parent || parent;
            value.path = value.path || binding;
            return value;
        }

        if (isArrayOfEntities(value) || (value instanceof Array && isJsonArray(value))) {
            return new CpEntityList(this, value, binding, parent);
        }

        //77954 come gestire il caso array???
        // const datasetNode = this.datasetService.getNode(binding);
        // if (datasetNode?.isDynamicDataSource) {
        //     if (Array.isArray(value)) {
        //
        //     } else {
        //         value.Id = new Guid().toString();
        //     }
        // }

        return new CpEntity(this, value as JsonObject, binding, parent);
    }

    makeEntityFromDataset(
        binding: DataBinding,
        parent?: ICpEntity,
        ...advancedBindingValues: (string | null)[]
    ): ICpEntity | null {
        const model = this.datasetService.getPlaceholder(binding, ...advancedBindingValues);
        let entityBinding = binding;
        for (const value of advancedBindingValues) {
            entityBinding = entityBinding.addAdvanced(value);
        }

        if (model) {
            model.Id = new Guid().toString();
        }

        return this.makeEntity(model ?? null, entityBinding, parent);
    }

    createOrFindParent(path: DataBinding) {
        const bindingPartStack: DataBindingPart[] = [];
        let match: ICpEntity | undefined = undefined;

        while (!path.isEmpty()) {
            const entity = this.model?.getValue(path);
            if (isEntity(entity)) {
                match = entity;
                break;
            }

            bindingPartStack.push(path.property);
            path = path.entity;
        }
        if (!match) {
            // Per retrocompatibilità, magari lanciare eccezione in futuro?
            return undefined;
        }

        let iterAncestor = match;
        while (bindingPartStack.length !== 0) {
            const part = bindingPartStack.pop()!;
            path = path.add(part);
            const placeholder = this.datasetService.getPlaceholder(path);
            if (!placeholder) {
                // È possibile che l'ultimo pezzo fallisca, perché è possibile che il binding sia su un campo.
                // In questo caso il padre è l'ultima entity creata.
                if (bindingPartStack.length === 0) {
                    return iterAncestor;
                }
                // Per retrocompatibilità, magari lanciare eccezione in futuro?
                return undefined;
            }
            const entity = this.makeEntity(placeholder, path, match);

            iterAncestor.setValue(new DataBinding([part]), entity, false);
            iterAncestor = entity;
        }

        return iterAncestor;
    }

    createEntityForVirtualPanel(dataBindingString: string, itemSource?: IDataSourceItem): ICpEntity | null {
        const dataBinding = new DataBinding(dataBindingString);
        const simple = dataBinding.toStringSimple();
        if (dataBinding.isAdvanced() && itemSource) {
            const parentEntity = this.getEntity(new DataBinding(simple).entity);
            if (!parentEntity) return null; //gestire come errore?
            const parentPath = new DataBinding(simple).entity;
            const newEntity = this.makeEntityFromDataset(parentPath, parentEntity, itemSource.Id);
            if (!newEntity) return null; //gestire come errore?
            parentEntity.addEntityToList(new DataBinding(), newEntity, false);
            this.notifyBindingsAndConstraints(parentPath, newEntity);
            return newEntity;
        } else if (itemSource) {
            return this.createModelFromPlaceholder(dataBinding.entity, dataBinding.entity.toStringSimple(), {
                name: dataBinding.propertyName,
                value: itemSource as unknown as JsonPrimitive,
            });
        } else {
            return this.createModelFromPlaceholder(dataBinding, simple);
        }
    }

    /**
     * @deprecated Usare makeEntityFromDataset
     */
    makeEntityFromPlaceholder(binding: DataBinding, parent?: ICpEntity) {
        const model = cloneDeep(this.placeholders[binding.toStringSimple()]);
        if (!model) {
            // Per es. combo.
            return null;
        }
        model.Id = new Guid().toString();

        return this.makeEntity(model, binding, parent);
    }

    isUndoEntity(entity: PropertyType | undefined) {
        return entity instanceof CpUndoEntity;
    }

    makeUndoEntity(originalEntity: ICpEntity): ICpEntity {
        return new CpUndoEntity(this, originalEntity);
    }

    //#endregion

    //#region Private Methods

    /**
     * Controlla se due valori sono "uguali" in termini di update del modello.
     */
    private isPropertyValueEqual(a: PropertyType | undefined, b: PropertyType | undefined): boolean {
        // Null / undefined
        if (a === null || a === undefined) {
            return a == b;
        }

        // Tipi primitivi
        if (a === b) {
            return true;
        }

        // Moment
        if (isMoment(a)) {
            return this.isPropertyMomentEqual(a, b);
        }
        if (isMoment(b)) {
            return this.isPropertyMomentEqual(b, a);
        }

        // Array primitivo
        if (isPropertyPrimitive(a)) {
            return isEqual(a, b);
        }

        // Array di entity
        if (a instanceof Array || a instanceof CpEntityList) {
            return this.isPropertyEntityArrayEqual(a, b);
        }

        // Entity
        if (isEntity(a) && isEntity(b)) {
            return this.isEntityEqual(a, b);
        }

        return false;
    }

    private isEntityEqual(a: ICpEntity, b: ICpEntity) {
        if (a.Id && b.Id) {
            return a.Id === b.Id;
        }
        const xj = 'toJSON' in a ? a.toJSON() : a;
        const yj = 'toJSON' in b ? b.toJSON() : b;

        return isEqual(xj, yj);
    }

    private isPropertyEntityArrayEqual(a: CpEntityList | ICpEntity[], b: PropertyType | undefined) {
        // Array di valori non è mai uguale ad array di entity.
        if (isPropertyPrimitive(b)) {
            return false;
        }

        if (!(b instanceof Array) && !(b instanceof CpEntityList)) {
            return false;
        }

        const ae = a instanceof CpEntityList ? a.activeEntities : a;
        const be = b instanceof CpEntityList ? b.activeEntities : b;

        return ae.length === be.length && isEmpty(differenceWith(ae, be, (x, y) => this.isEntityEqual(x, y)));
    }

    private isPropertyMomentEqual(a: Moment, b: unknown) {
        if (isMoment(b)) {
            return b.isSame(a);
        }
        if (typeof b === 'string') {
            return a.isSame(moment(b, moment.ISO_8601));
        }
        return false;
    }

    private notifyAllBindingsAndConstraints() {
        for (const [binding, listener] of this.modelListeners) {
            listener.next(this.model?.getValue(new DataBinding(binding)));
        }

        this.constraintNotifier.notifyAllConstraint();
    }

    private notifyBindingsAndConstraints(binding: DataBinding, value: PropertyType | undefined) {
        if (binding.isEmpty()) {
            return this.notifyAllBindingsAndConstraints();
        }

        // Notify constraints for this binding and its direct parent.
        this.constraintNotifier.notifyValueChanged(binding);
        this.constraintNotifier.notifyValueChanged(binding.entity);

        /*@TODO cosa fare per i childrenarray? bisogna non rimuovere gli indici???
         *   per i binding avanzati nei children array il problema e` che non vengono notificati i cambiamenti ai controlli
         * mentre tutto avviene correttamente nel modelservice
         * */
        let bindingKey = binding.toStringForNotification();

        // Notify all children.
        for (const [k, listener] of this.modelListeners) {
            if (k !== bindingKey && k.startsWith(bindingKey)) {
                listener.next(this.model?.getValue(new DataBinding(k)));
            }
        }

        // Notify the binding.
        if (isEntity(value)) {
            this.notifyBinding(value);
        } else {
            /** notifico i listener con e senza indici */
            this.modelListeners.get(bindingKey)?.next(value);
            const fullBinding = binding.toString();
            if (fullBinding !== bindingKey) {
                this.modelListeners.get(fullBinding)?.next(value);
            }
        }

        // Notify all ancestors.
        let entity = isEntity(value) ? value.parent : this.model?.getEntity(binding.entity);
        while (entity) {
            this.notifyBinding(entity);
            entity = entity.parent;
        }

        // Notify the root.
        this.modelListeners.get('')?.next(this.model);
    }

    private notifyBinding(entity: ICpEntity) {
        if (!entity.path) {
            this.logger.error(`Entity has no path! Id: "${entity.Id}", Parent: "${entity.parent?.path}"`);
            return;
        }

        const simpleKey = entity.path.toStringForNotification();
        this.modelListeners.get(simpleKey)?.next(entity);
        const fullKey = entity.path.toString();
        if (fullKey !== simpleKey) {
            this.modelListeners.get(fullKey)?.next(entity);
        }
        if (entity.path.isEmpty()) {
            return;
        }

        // Oltre a notificare gli indici notifica anche i binding avanzati e viceversa.
        if (entity.path.property?.isIndex()) {
            const bindingKeys = this.datasetService.getAdvancedBindingKeys(entity.path);
            if (!bindingKeys) {
                /*se index ma non advanced non si notifica...perche????*/
                return;
            }

            let advPath = entity.path.entity;
            for (const key of bindingKeys) {
                const bindingValue = entity.getPropertyValue(key);
                if (bindingValue && typeof bindingValue === 'object' && 'Id' in bindingValue) {
                    advPath = advPath.addAdvanced(bindingValue.Id);
                }
            }
            this.modelListeners.get(advPath.toStringForNotification())?.next(entity);
        } else if (entity.path.property?.isAdvanced() && entity.parent && entity.parent instanceof CpEntityList) {
            const idxKey = entity.path.entity.addIndex(entity.parent.getEntityIndex(entity)).toString();
            this.modelListeners.get(idxKey)?.next(entity);
        }
    }

    //#endregion
}
